import {
  BellOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Typography,
} from "antd";
import { useFormik } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import {
  ChangePassword,
  changePassword,
  logout,
  notifications,
} from "../../actions/auth";
import { corporateBudget } from "../../actions/cards/cards";
// import rblLogo from "../../assets/images/rbl-logo.svg";
import Logo from "../../assets/images/logo-1.png";
import { amountFormat, hasAccess, objectToFormData } from "../../utils";
import BaseCurrency from "../Common/BaseCurrency";
import Notifications from "../Common/Notifications";
import PasswordCheck from "../Common/PasswordCheck";
import "./header.css";
import ProfilePopUp from "./ProfilePopUp";

const { Option } = Select;
const { Text, Title } = Typography;

const ValidationSchema = yup.object().shape({
  current_password: yup
    .string()
    .trim()
    .required("Current Password is required")
    .min(12),
  password: yup
    .string()
    .trim()
    .required("Password is required")
    .min(12)
    .notOneOf(
      [yup.ref("current_password"), null],
      "Current Password and Password must not match",
    ),
  password_confirmation: yup
    .string()
    .trim()
    .required("Password Confirmation is required")
    .min(12)
    .oneOf([yup.ref("password"), null], "Passwords must match"),
});

message.config({
  maxCount: 3,
});

const Header = () => {
  const dispatch = useDispatch();
  const is_admin = useSelector(
    (state) => state?.auth?.localVariables?.is_admin,
  );
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [isProfileVisible, setIsProfileVisible] = React.useState(false);
  const [isNoficationVisible, setIsNoficationVisible] = React.useState(false);
  const [passwordCheck, setPasswordCheck] = React.useState(true);
  const [selectedOption, setSelectedOption] = React.useState("admin");
  const formik = useFormik({
    initialValues: {
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    enableReinitialize: true,
    validationSchema: ValidationSchema,
    onSubmit: (vals) => {
      dispatch(changePassword(objectToFormData(vals)));
    },
  });
  const changePasswordResponse = useSelector(
    (state) => state.auth.changePassword || {},
  );
  const corporate_budget = useSelector(
    (state) => state.cards.corporate_budget?.data?.response,
  );

  const corporate_budget_loading = useSelector(
    (state) => state.cards.corporate_budget?.loading,
  );
  const unseen_count = useSelector(
    (state) =>
      state?.auth?.notifications?.data?.response?.unseen_notifications_count,
  );

  React.useEffect(() => {
    if (hasAccess("account", "read")) dispatch(corporateBudget());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (is_admin) {
      dispatch(notifications());
    } else {
      dispatch(notifications({ is_employee: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is_admin]);

  React.useEffect(() => {
    const { data: { message: res_message = "", status = "" } = {} } =
      changePasswordResponse;
    if (status) {
      setIsModalVisible(false);
      setSelectedOption("admin");
      dispatch({
        type: ChangePassword.RESET,
      });
      formik.resetForm();
      dispatch(logout());
    } else if (status === false) {
      dispatch({
        type: ChangePassword.RESET,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changePasswordResponse]);

  const handleSelection = (value) => {
    if (value === "change_password") {
      setIsModalVisible(true);
    } else if (value === "profile") {
      setIsProfileVisible(true);
    } else if (value === "logout") {
      dispatch(logout());
    }
    setSelectedOption("admin");
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedOption("admin");
    formik.resetForm();
  };

  const renderChangePassword = () => {
    return (
      <div className="psd_content">
        <Title level={3} className="mt1">
          Set your new password
        </Title>
        <Text>
          Please create a new password below. Your new password needs to have a
          minimum of 12 characters.
        </Text>
        <br />
        <br />
        <Form onFinish={formik.handleSubmit} className="mt2" layout="vertical">
          <Form.Item
            label="Current Password"
            placeholder="Enter Current Password"
            help={
              formik.touched.current_password && formik.errors.current_password
            }
          >
            <Input.Password
              placeholder="Enter password"
              value={formik.values.current_password}
              onChange={(e) => {
                formik.setFieldValue(
                  "current_password",
                  e.target.value?.trim(),
                );
              }}
              name="current_password"
            />
          </Form.Item>
          <Form.Item
            label="New Password"
            placeholder="Enter New Password"
            help={formik.touched.password && formik.errors.password}
          >
            <Input.Password
              placeholder="Enter password"
              value={formik.values.password}
              onChange={(e) => {
                formik.setFieldValue("password", e.target.value?.trim());
              }}
              name="password"
            />
            <PasswordCheck
              password={formik.values.password}
              setPasswordCheck={setPasswordCheck}
            />
          </Form.Item>
          <Form.Item
            label="Confirm New Password"
            placeholder="Enter New Password"
            help={
              formik.touched.password_confirmation &&
              formik.errors.password_confirmation
            }
          >
            <Input.Password
              placeholder="Enter password"
              value={formik.values.password_confirmation}
              onChange={(e) => {
                formik.setFieldValue(
                  "password_confirmation",
                  e.target.value?.trim(),
                );
              }}
              name="password_confirmation"
            />
          </Form.Item>

          <Button
            htmlType="submit"
            className="primaryButton mt1"
            disabled={passwordCheck || changePasswordResponse?.loading}
          >
            Change Password
          </Button>
          <Button
            className="cancelButton ml1 mt1"
            onClick={() => handleCancel()}
          >
            Cancel
          </Button>
        </Form>
      </div>
    );
  };

  return (
    <div className="header-div mb1">
      {isModalVisible && (
        <Modal
          title="Change Password"
          visible={isModalVisible}
          onCancel={handleCancel}
          footer={[]}
          className="center-aligned-modal"
        >
          {renderChangePassword()}
        </Modal>
      )}

      {isProfileVisible && (
        <Modal
          title="Card Holder Details"
          visible={isProfileVisible}
          onCancel={() => {
            setIsProfileVisible(false);
          }}
          footer={[]}
          className="center-aligned-modal"
          width={550}
        >
          <ProfilePopUp />
        </Modal>
      )}
      <Row
        style={{
          alignItems: "center",
        }}
      >
        <Col span={9} className="logoContainer">
          {/* <div className="header-logo-section">
            <img src={Logo} alt={"logo"} className="header-logo ml2" />
          </div> */}
          <img src={Logo} alt={"logo"} className="header-logo ml2" />
        </Col>
        <Col
          span={
            sessionStorage.getItem("is_admin") === "corporate" ||
            sessionStorage.getItem("is_admin") === "false" ||
            !hasAccess("account", "read")
              ? 9
              : 4
          }
          style={{
            ...(sessionStorage.getItem("is_admin") === "false" ||
            !hasAccess("account", "read")
              ? { marginRight: "3rem" }
              : {}),
          }}
        ></Col>
        {sessionStorage.getItem("is_admin") === "true" &&
          hasAccess("account", "read") && (
            <Col span={6}>
              <div className="head-budget mr2">
                <Text className="text-dim">Corporate Budget</Text>
                <Title level={3} style={{ marginTop: "0rem" }}>
                  <BaseCurrency />{" "}
                  {corporate_budget?.currentLimit &&
                    amountFormat(corporate_budget?.currentLimit)}{" "}
                  &nbsp;{" "}
                  {corporate_budget_loading ? (
                    <LoadingOutlined />
                  ) : (
                    <ReloadOutlined
                      className="cursor"
                      onClick={() => {
                        dispatch(corporateBudget());
                      }}
                    />
                  )}
                </Title>
              </div>
            </Col>
          )}
        <Col span={1} style={{ position: "relative", textAlign: "end" }}>
          <Badge
            className="cursor badge-section"
            count={unseen_count ?? 0}
            overflowCount={99}
            onClick={() => {
              setIsNoficationVisible((bool) => !bool);
            }}
          >
            <BellOutlined style={{ color: "#fff", fontSize: "20px" }} />
          </Badge>
          {isNoficationVisible && (
            <Notifications
              onClose={() => {
                setIsNoficationVisible(false);
              }}
            />
          )}
        </Col>
        <Col span={4} className="d-flex-center">
          <div className="user-info-section">
            <div className="user-avatar">
              {sessionStorage.getItem("name")?.[0]?.toUpperCase()}
            </div>
            <Select
              value={selectedOption}
              className="header-drop"
              onChange={(val) => handleSelection(val)}
              suffixIcon={<span style={{ color: "black" }}>▼</span>}
              style={{ width: "11rem" }}
            >
              <Option value="admin">{sessionStorage.getItem("name")}</Option>
              {is_admin === false && <Option value="profile">Profile</Option>}
              <Option value="change_password">Change Password</Option>
              <Option value="logout">Logout</Option>
            </Select>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default Header;
